import { useState, useMemo, useCallback, useEffect } from "react";

// Component Imports
import {
  Button,
  DropDownInput,
  InputField,
  RadioInput,
  DateInput,
  MiniTabs,
  DataTable,
} from "@/components";

// Icons
import { RiFileExcel2Line } from "react-icons/ri";
import { MdOutlineSync } from "react-icons/md";
// Data & Utilities
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilter, useApi, usePersistentFilters } from "@/"; // Assuming the hook is exported from index
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { fetchShipmentData, selectAllShipments } from "../../app/Slices";
import { subDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import OptionGroup from "@/components/OptionGroup";

const Tracking = () => {
  /* ----------------------- State Definitions ----------------------- */
  // Filters state
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const navigate = useNavigate();
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const dispatch = useDispatch();
  const { callApi } = useApi();

  // Use persistent filters hook instead of regular useState
  const [filters, setFilters] = usePersistentFilters({
    orderId: "",
    courierName: "",
    channelName: "",
    paymentMode: "",
    serviceMode: "",
    awbs: "",
    shippingId: "",
    customerName: "",
    customerId: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  // Applied filters for triggering filtering
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Active tab state (for MiniTabs)
  const [activeTab, setActiveTab] = useState("all");

  // Selected rows for DataTable
  const [selectedRows, setSelectedRows] = useState([]);

  // Reset page counter for DataTable pagination
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchShipmentData({
          startDate: appliedFilters.date.from,
          endDate: appliedFilters.date.to,
        }),
      );
      if (fetchShipmentData.fulfilled.match(response)) {
        console.log("Shipment data fetched successfully");
      } else if (fetchShipmentData.rejected.match(response)) {
        console.error(
          "Error fetching shipment data:",
          response?.error || "Unknown Error",
        );
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
    }
  }, [dispatch, appliedFilters.date.from, appliedFilters.date.to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const shipments = useSelector(selectAllShipments) || [];

  const courier =
    useSelector((state) => {
      const courierData = state.app?.appData?.courier || [];
      if (courierData.length > 0) {
        // Store in sessionStorage when we have data
        sessionStorage.setItem("courierData", JSON.stringify(courierData));
        return courierData;
      }
      // Retrieve from sessionStorage if redux store is empty
      const storedCourier = sessionStorage.getItem("courierData");
      return storedCourier ? JSON.parse(storedCourier) : [];
    }) || [];

  // Filter configuration for useFilteredData
  const filterConfig = useMemo(
    () => ({
      orderId: {
        type: "comma-api",
        itemKey: "userOrderId",
        model: "Shipment",
        endpoint: "/common/search",
      },
      courierName: { type: "partial", itemKey: "courierId" },
      channelName: { type: "exact", itemKey: "order.channel" },
      customerName: {
        type: "partical",
        itemKey: "user.name",
      },
      customerId: { type: "partial", itemKey: "user.clientId" },
      shippingId: {
        type: "comma",
        itemKey: "shipmentId",
      },
      awbs: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      paymentMode: { type: "exact", itemKey: "order.paymentMode" },
      serviceMode: { type: "exact", itemKey: "serviceMode" },
      date: { type: "date", itemKey: "shipmentDate" },
    }),
    [],
  );

  // Tab filter configuration as a serializable object.
  const tabConfig = useMemo(
    () => ({
      idKey: "status",
      allTabId: "all",
      filters: {
        pendingPickup: { type: "exact", value: "Pending" },
        inTransit: { type: "exact", value: "In Transit" },
        outForDelivery: { type: "exact", value: "Out for Delivery" },
        delivered: { type: "exact", value: "Delivered" },
        cancelled: { type: "exact", value: "Cancelled" },
        lost: { type: "exact", value: "Lost" },
        damaged: { type: "exact", value: "Damaged" },
        rtoAll: { type: "exact", value: "RTO" },
        rtoInTransit: { type: "exact", value: "RTO In Transit" },
        rtoDelivered: { type: "exact", value: "RTO Delivered" },
        rtoLost: { type: "exact", value: "RTO Lost" },
      },
    }),
    [],
  );

  const handleView = useCallback((order) => {
    console.log("View Shipment:", order);
    navigate(`orderDetails`, { state: { orderData: order } });
  }, []);
  const handleSearch = (type, value) => {
    if (!value) return; // guard clause to avoid undefined or null
    window.open(
      `https://www.indiancouriercompany.com/tracking?${type}=${value}`,
      "_blank",
    );
  };
  // Apply filtering using useFilteredData hook.
  const {
    data: finalFilteredData,
    counts,
    isLoading,
  } = useFilter(shipments, appliedFilters, filterConfig, activeTab, tabConfig);
  /* ----------------------- Memoized Values ----------------------- */
  // Channel Options for DropDownInput
  const channel = useSelector((state) => state.app.appData.channels) || [];

  const channelOptions = useMemo(
    () => channel.map((c) => ({ value: c.name, label: c.name })),
    [channel],
  );

  // Courier Options for DropDownInput
  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    [],
  );

  // Payment Options for RadioInput
  const paymentOptions = useMemo(
    () => [
      { value: "Prepaid", label: "Prepaid" },
      { value: "COD", label: "COD" },
    ],
    [],
  );
  const serviceMode = useMemo(
    () => [
      { value: "Air", label: "Air" },
      { value: "Surface", label: "Surface" },
    ],
    [],
  );

  // Bulk Actions for OptionGroup
  const options = useMemo(
    () => [
      {
        label: "Tracking Update",
        value: "tracking_update",
        icon: <MdOutlineSync />,
      },
      {
        label: "Due Update",
        value: "due_update",
        icon: <MdOutlineSync />,
      },
    ],
    [],
  );

  // Tabs for MiniTabs (static counts for now)
  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts?.all || 0 },
      {
        id: "pendingPickup",
        label: "Pending Pickup",
        count: counts?.pendingPickup || 0,
      },
      { id: "inTransit", label: "In Transit", count: counts?.inTransit || 0 },
      {
        id: "outForDelivery",
        label: "Out for Delivery",
        count: counts?.outForDelivery || 0,
      },
      { id: "delivered", label: "Delivered", count: counts?.delivered || 0 },
      { id: "cancelled", label: "Cancelled", count: counts?.cancelled || 0 },
      { id: "lost", label: "Lost", count: counts?.lost || 0 },
      { id: "damaged", label: "Damaged", count: counts?.damaged || 0 },
      { id: "rtoAll", label: "RTO All", count: counts?.rtoAll || 0 },
      {
        id: "rtoInTransit",
        label: "RTO In Transit",
        count: counts?.rtoInTransit || 0,
      },
      {
        id: "rtoDelivered",
        label: "RTO Delivered",
        count: counts?.rtoDelivered || 0,
      },
      { id: "rtoLost", label: "RTO Lost", count: counts?.rtoLost || 0 },
    ],
    [counts],
  );
  // Format currency values
  const formatCurrency = useCallback((value) => {
    // Check if value is valid number
    if (value === null || value === undefined || isNaN(value) || value === "") {
      return "N/A";
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return "N/A";
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(numValue);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const statusStyle = {
    Pending: "bg-yellow-100/90 text-yellow-800",
    Pickup: "bg-yellow-100/90 text-yellow-800",
    "In Transit": "bg-orange-100 text-orange-800",
    Rescheduled: "bg-orange-100 text-orange-800",
    "Out for Delivery": "bg-purple-200/80 text-purple-800",
    Delivered: "bg-green-100 text-green-800",
    "Partially Delivered": "bg-green-100 text-green-800",
    Cancelled: "bg-cyan-800/20 text-cyan-800",
    Lost: "bg-slate-900/20 text-slate-900",
    Damaged: "bg-amber-900/20 text-amber-900",
    "RTO All": "bg-red-300/30 text-red-800",
    Exception: "bg-red-300/30 text-red-800",
    RTO: "bg-red-300/30 text-red-800",
    "RTO In-Transit": "bg-orange-100 text-orange-800",
    "RTO In Transit": "bg-orange-100 text-orange-800",
    "RTO Delivered": "bg-green-100 text-green-800",
    "RTO Lost": "bg-slate-900/20 text-slate-900",
  };

  // Columns for DataTable
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "user.name",
        meta: {
          tooltip: (row) => row?.user?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "user.clientId",
        meta: {
          tooltip: (row) => row?.user?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Channel",
        accessorKey: "order.channel",
        meta: {
          tooltip: (row) => row.order?.channel || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "userOrderId",
        cell: (info) => (
          <span
            onClick={() => handleView(info.row.original)}
            className="underline text-blue-600 cursor-pointer"
          >
            {info.row.original.userOrderId}
          </span>
        ),
        meta: {
          tooltip: (row) => row?.userOrderId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Booking Date",
        accessorKey: "shipmentDate",
        cell: (info) => formatDate(info.row.original.shipmentDate),
        meta: {
          tooltip: (row) => formatDate(row?.shipmentDate || "N/A"),
        },
      }),
      ColumnHelper({
        header: "Shipping ID",
        accessorKey: "shipmentId",
        cell: (info) => {
          const shipmentId = info.row.original.shipmentId;
          return (
            <span
              onClick={() => handleSearch("shipmentId", shipmentId)}
              className="underline text-blue-600 cursor-pointer"
            >
              {shipmentId || "N/A"}
            </span>
          );
        },
        meta: {
          tooltip: (row) => row.shipmentId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Products",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.order?.products
            .map((product) => product.productName)
            .join(", ");
          return <span>{products}</span>;
        },
        meta: {
          tooltip: (row) =>
            row.order?.products.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "payment",
        cell: (info) => {
          const row = info.row.original;
          // Try multiple possible field paths for payment amount
          const orderValue =
            row.order?.totalOrderValue ||
            row.order?.totalAmount ||
            row.order?.amount ||
            row.totalOrderValue ||
            row.totalAmount ||
            row.amount ||
            row.order?.orderValue ||
            row.orderValue;

          return (
            <span className="font-nunito">{formatCurrency(orderValue)}</span>
          );
        },
        meta: {
          tooltip: (row) => {
            const orderValue =
              row.order?.totalOrderValue ||
              row.order?.totalAmount ||
              row.order?.amount ||
              row.totalOrderValue ||
              row.totalAmount ||
              row.amount ||
              row.order?.orderValue ||
              row.orderValue;
            return formatCurrency(orderValue);
          },
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "paymentMode",
        meta: {
          tooltip: (row) => row.order?.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Service Mode",
        accessorKey: "serviceMode",
        meta: {
          tooltip: (row) => row.serviceMode,
        },
      }),
      ColumnHelper({
        header: "Client Name",
        accessorKey: "deliveryAddress.customerName",
        meta: {
          tooltip: (row) => row.deliveryAddress.customerName,
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber,
        },
      }),
      ColumnHelper({
        header: "Weight",
        accessorKey: "order.physicalWeight",
        cell: (info) =>
          `${info.row.original.order?.physicalWeight || "N/A"} kg`,
        meta: {
          tooltip: (row) => `${row.order?.physicalWeight || "N/A"} kg`,
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
              statusStyle[info.row.original.status]
            }`}
          >
            {info.row.original.status}
          </span>
        ),
        meta: {
          tooltip: (row) => row.status,
        },
      }),
    ],
    [],
  );

  /* ----------------------- Handlers ----------------------- */
  // Update filter state - now automatically persists to URL
  const handleFilterChange = (field, value) => {
    setFilters({
      [field]: value,
    });
  };

  // Apply filters
  const applyFilters = () => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  };

  // Reset all filters
  const resetFilters = () => {
    const initialFilters = {
      orderId: "",
      courierName: "",
      channelName: "",
      customerId: "",
      customerName: "",
      paymentMode: "",
      serviceMode: "",
      shippingId: "",
      awbs: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  };

  const handleTabChange = useCallback((tab) => {
    console.log("Tab Changed:", tab);
    setActiveTab(tab);
  }, []);

  //Bulk Actions Handler
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);

    if (value === "tracking_update") {
      handleTrackingUpdate();
    }
    if (value === "due_update") {
      handleDueUpdate();
    }
  };

  // Due Update Handler
  const handleDueUpdate = async () => {
    if (selectedRows.length === 0) {
      console.log("No rows selected for due update");
      return;
    }

    // Extract shipment IDs from selected rows
    const shipmentIds = selectedRows
      .map((row) => row.shipmentId)
      .filter((id) => id && id.trim() !== "");

    if (shipmentIds.length === 0) {
      console.log("No valid shipment IDs found in selected rows");
      return;
    }

    console.log("Due update requested for shipments:", shipmentIds);

    try {
      const response = await callApi(
        "/admin/dataRecovery/recoverDues",
        "POST",
        {
          shipmentIds: shipmentIds,
        },
      );

      if (response?.success) {
        console.log("Due update successful for selected shipments");
        // Refresh data to show updated due information
        await fetchData();
      } else {
        console.error("Due update failed:", response);
      }
    } catch (error) {
      console.error("Error updating dues:", error);
    }
  };
  //Tracking Update Handler
  const handleTrackingUpdate = async () => {
    if (selectedRows.length === 0) {
      console.log("No rows selected for tracking update");
      return;
    }

    // Extract AWB numbers from selected rows
    const awbNumbers = selectedRows
      .map((row) => row.awbNumber)
      .filter((awb) => awb && awb.trim() !== "");

    if (awbNumbers.length === 0) {
      console.log("No valid AWB numbers found in selected rows");
      return;
    }

    console.log("Tracking update requested for AWBs:", awbNumbers);

    try {
      // Group AWBs by courier for efficient API calls
      const courierGroups = selectedRows.reduce((groups, row) => {
        const courierId = row.courierId;
        if (!groups[courierId]) {
          groups[courierId] = {
            courier:
              courier.find((c) => c._id === courierId)?.name || "Unknown",
            awbNumbers: [],
          };
        }
        if (row.awbNumber && row.awbNumber.trim() !== "") {
          groups[courierId].awbNumbers.push(row.awbNumber);
        }
        return groups;
      }, {});

      const updatePromises = Object.values(courierGroups).map(async (group) => {
        return callApi("/admin/updateTracking", "POST", {
          courier: group.courier,
          awbs: group.awbNumbers,
        });
      });

      const responses = await Promise.allSettled(updatePromises);

      let successCount = 0;
      let errorCount = 0;

      responses.forEach((response, index) => {
        if (response.status === "fulfilled" && response.value?.success) {
          successCount++;
        } else {
          errorCount++;
          console.error(
            `Tracking update failed for courier group ${index + 1}:`,
            response.status === "rejected" ? response.reason : response.value,
          );
        }
      });

      if (successCount > 0) {
        console.log(
          `Tracking update successful for ${successCount} courier groups`,
        );
        // Refresh data to show updated tracking information
        await fetchData();
      }

      if (errorCount > 0) {
        console.warn(`Tracking update failed for ${errorCount} courier groups`);
      }
    } catch (error) {
      console.error("Error updating tracking:", error);
    }
  };

  const handleDateChange = useCallback(
    (dateRange) => {
      console.log("Date changed:", dateRange);
      setFilters({
        date: {
          from: dateRange?.from || startOfMonthDate,
          to: dateRange?.to || endOfMonthDate,
        },
      });
    },
    [setFilters, startOfMonthDate, endOfMonthDate],
  );
  // Export Handler

  /* ----------------------- Render ----------------------- */
  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="w-full flex flex-col gap-2 md:flex-row justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            Track Shipments
          </h2>
          {/* <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleExport}
              icon={RiFileExcel2Line}
            >
              Export
            </Button>
          </div> */}
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            mode="range"
            placeholder="Select From & To Date"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Order IDs Comma Separated"
            value={filters.orderId}
            onChange={(value) => handleFilterChange("orderId", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            value={filters.customerId}
            onChange={(val) => handleFilterChange("customerId", val || "")}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            value={filters.customerName}
            onChange={(val) => handleFilterChange("customerName", val || "")}
          />
        </div>

        {/* Additional Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName}
            onChange={(value) => handleFilterChange("courierName", value)}
          />
          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={channelOptions}
            value={filters.channelName}
            onChange={(value) => handleFilterChange("channelName", value)}
          />

          <InputField
            label="AWBs"
            placeholder="AWBs Comma Separated"
            value={filters.awbs}
            onChange={(value) => handleFilterChange("awbs", value)}
          />
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={paymentOptions}
            selectedValue={filters.paymentMode}
            onChange={(value) => handleFilterChange("paymentMode", value)}
          />
          <RadioInput
            label="Service Mode"
            name="service_mode"
            options={serviceMode}
            selectedValue={filters.serviceMode}
            onChange={(value) => handleFilterChange("serviceMode", value)}
          />
          <InputField
            label="Shipping ID"
            placeholder="Shipping IDs Comma Separated "
            value={filters.shippingId}
            onChange={(value) => handleFilterChange("shippingId", value)}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={applyFilters}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={resetFilters}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <MiniTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          showMore={true}
        />

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}

        {/* DataTable */}
        <DataTable
          columns={columns}
          tittle="Shipments"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          showCheckbox={
            import.meta.env.VITE_NODE_ENV &&
            import.meta.env.VITE_NODE_ENV === "development"
              ? true
              : false
          }
          isLoading={isLoading}
          resetPage={resetPageCounter}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export Tracking",
                filename: `Shipments${new Date().getTime()}`,
                date: {
                  from: appliedFilters.date?.from?.toString(),
                  to: appliedFilters.date?.to?.toString(),
                },
              },
            },
          ]}
        />
      </div>
    </SpaceWrapper>
  );
};

export default Tracking;
